import { Stack } from '@mui/material';
import { cloneElement, useMemo } from 'react';

import {
  getIconForSemanticTileNode,
  getSemanticTileColors,
  SemanticTileKindNode,
} from '@/components/diagrams/components/Tile/Tile.utils';
import { RecipientKind } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { ClientOrganizationKind, EntityInEstateStatus } from '@/types/schema';
import { PulidKind } from '@/utils/pulid';

import { useDispositiveProvisionsContext } from '../../contexts/dispositiveProvisions.context';
import { useGetTileNodeQuery } from './graphql/GetTileNode.generated';

const sizeMap = {
  small: {
    size: 12,
    padding: '6px',
  },
  large: {
    size: 20,
    padding: '10px',
  },
};

interface DispositiveProvisionTileIconProps {
  size: 'small' | 'large';
  node?: SemanticTileKindNode;
  nodeId?: string;
  // Color shade of the badge: 'solid' (default) fills with the strong color +
  // white glyph; 'subtle' uses a light-tinted background + darker glyph,
  // matching the beneficiary-designations cards.
  variant?: 'solid' | 'subtle';
  // Badge shape. Defaults to a circle for 'solid' and a rounded square for
  // 'subtle' (the recipient-card look). Pass explicitly to mix shade and shape
  // — e.g. a light circle for section / disposing-object headers.
  shape?: 'circle' | 'roundedSquare';
}

export function DispositiveProvisionTileIcon({
  size,
  node: externalNode,
  nodeId,
  variant = 'solid',
  shape = variant === 'subtle' ? 'roundedSquare' : 'circle',
}: DispositiveProvisionTileIconProps) {
  const { primaryClients } = useDispositiveProvisionsContext();

  const { data } = useGetTileNodeQuery({
    skip: !nodeId,
    variables: { id: nodeId! },
  });

  const node: SemanticTileKindNode | null = useMemo(() => {
    if (externalNode) {
      return externalNode;
    }

    if (data?.node) {
      const inEstateStatus = (() => {
        if (
          data.node.__typename === 'Entity' &&
          data.node.subtype &&
          'inEstateStatus' in data.node.subtype
        ) {
          return data.node.subtype.inEstateStatus ?? undefined;
        }

        if (data.node.__typename === 'TestamentaryEntity') {
          return data.node.inEstateStatus ?? undefined;
        }

        if (data.node.__typename === 'ClientProfile') {
          if (primaryClients.find((c) => c.id === data.node?.id)) {
            return EntityInEstateStatus.InEstate;
          }

          return EntityInEstateStatus.OutOfEstate;
        }

        return undefined;
      })();

      return {
        __typename: data.node.__typename as SemanticTileKindNode['__typename'],
        isCharitableOrganization:
          data.node.__typename === 'ClientOrganization'
            ? data.node.clientOrganizationKind ===
              ClientOrganizationKind.CharitableOrganization
            : undefined,
        entityKind:
          data.node.__typename === 'Entity' ? data.node.entityKind : undefined,
        inEstateStatus,
        testamentaryEntityKind:
          data.node.__typename === 'TestamentaryEntity'
            ? data.node.testamentaryEntityKind
            : undefined,
      };
    }

    return null;
  }, [data?.node, externalNode, primaryClients]);

  const colors = useMemo(
    () => getSemanticTileColors(node ?? undefined, variant),
    [node, variant]
  );

  const tileIconElement = useMemo(() => {
    if (!node) {
      return null;
    }

    const icon = getIconForSemanticTileNode(node);

    // The icon resolver returns a rendered element. Clone it to apply the
    // badge-specific size and color.
    return icon
      ? cloneElement(icon, {
          size: sizeMap[size].size,
          color: colors.fg,
          sx: { display: 'flex' },
        })
      : null;
  }, [node, size, colors.fg]);

  if (!node) {
    return null;
  }

  return (
    <Stack
      padding={sizeMap[size].padding}
      bgcolor={colors.bg}
      borderRadius={shape === 'roundedSquare' ? '8px' : '50%'}
      justifyContent="center"
      alignItems="center"
    >
      {tileIconElement}
    </Stack>
  );
}

export function getTileIconTypenameFromRecipientKind(
  kind: RecipientKind | null | undefined
): SemanticTileKindNode['__typename'] {
  switch (kind) {
    case RecipientKind.Individual:
    case RecipientKind.SurvivingSpouse:
      return 'ClientProfile';
    case RecipientKind.Organization:
      return 'ClientOrganization';
    case RecipientKind.Entity:
      return 'Entity';
    case RecipientKind.TestamentaryEntity:
      return 'TestamentaryEntity';
    default:
      // TODO - ask what the default should be, like when adding a brand new recipient
      return 'Entity';
  }
}

export function getTileIconTypenameFromPulid(
  kind: PulidKind | null | undefined
): SemanticTileKindNode['__typename'] {
  switch (kind) {
    case PulidKind.ClientProfile:
      return 'ClientProfile';
    case PulidKind.ClientOrganization:
      return 'ClientOrganization';
    case PulidKind.Entity:
      return 'Entity';
    case PulidKind.TestamentaryEntity:
      return 'TestamentaryEntity';
    default:
      // TODO - ask what the default should be, like when adding a brand new recipient
      return 'Entity';
  }
}
